import { Checkbox, ConfigProvider, Divider, Pagination, Spin } from 'antd';
import { Asset, MarketPriceBySymbol, ReduxState, Wallet } from 'interfaces';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import CoinIcon from 'utilities/components/coinIcon/coinIcon.index';
import MyWalletBalanceCard from './balanceCard/balanceCard.index';
import { CiSearch } from 'react-icons/ci';
import { Button, Input, Table } from '../../components';
import { ColumnType } from 'antd/lib/table';
import { FaStar } from 'react-icons/fa';
import fa_IR from 'antd/locale/fa_IR';
import { WalletSummary } from '../walletSummary/walletSummary';
import { useNavigate } from 'react-router-dom';
import { get, priceDigit } from 'scripts';
import { API } from 'data/30pay.api';

interface OwnProps {
    wallet?: Wallet;
    loading: boolean;
    hideBalances: boolean;
    setHideBalances: Dispatch<SetStateAction<boolean>>;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const HIDDEN = '****';

const MyWallet: React.FC<Props> = (props) => {
    const PINNED_COINS = ['TRX', 'USDT', 'UUSD'];
    const [keyword, setKeyword] = useState<string>('');
    const [hideEmptyWallets, setHideEmptyWallets] = useState<boolean>(false);

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [pinCoinsData, setPinsCoinsData] = useState<Asset[]>();
    const navigate = useNavigate();

    useEffect(() => {
        fetchPinCoinData();
    }, [props.wallet]);

    function fetchPinCoinData() {
        if (props.wallet)
            Promise.all(PINNED_COINS.map((item) => get<MarketPriceBySymbol>(API.market.pricesBySymbol(item))))
                .then((responses) => {
                    const mergedArray: any = ['IRT'].concat(PINNED_COINS).map((pinCoinSymbol) => {
                        const asset = props.wallet?.assets.find(
                            (asset) => asset.symbol?.toUpperCase() === pinCoinSymbol.toLocaleUpperCase(),
                        );
                        const price =
                            pinCoinSymbol !== 'IRT'
                                ? responses[PINNED_COINS.findIndex((item) => item === pinCoinSymbol)].value.value
                                : { cryptoIrtBuyPrice: 1 };
                        return { ...asset, lastPrice: price.cryptoIrtBuyPrice };
                    });
                    setPinsCoinsData(mergedArray);
                })
                .catch((err) => setPinsCoinsData([]));
    }

    const columns: ColumnType<any>[] = [
        {
            title: '',
            dataIndex: 'id',
            key: 'id',
            render: (text, record: Asset) => {
                if (record?.symbol?.toUpperCase() === 'TRX' || record?.symbol?.toUpperCase() === 'USDT') {
                    return <FaStar className={'text-primary-100'} />;
                }
                return null;
            },
            width: 48,
        },
        {
            title: 'نام ارز',
            dataIndex: 'symbol',
            key: 'symbol',
            render: (text, record: Asset) => (
                <div className={'flex items-center gap-3'}>
                    <CoinIcon symbol={record?.symbol} className={'w-8 h-8'} />
                    <span className={'text-sm font-bold'}>{record?.name?.toUpperCase()}</span>
                    <span className={'text-xs font-medium en'}>{record?.symbol?.toUpperCase()}</span>
                </div>
            ),
        },
        {
            title: 'موجودی',
            dataIndex: 'balance',
            key: 'balance',
            align: 'center',
            render: (text, record: Asset) => (
                <span className={'font-medium text-sm'}>
                    {props.hideBalances
                        ? HIDDEN
                        : record.symbol === 'IRT'
                          ? priceDigit(record?.balance?.toFixed(0))
                          : '-'}
                </span>
            ),
        },
        {
            title: 'معادل دلاری',
            dataIndex: 'available',
            key: 'available',
            align: 'center',
            render: (text, record: Asset) =>
                record.symbol !== 'IRT' ? (
                    <span className={'font-medium text-sm'}>
                        {props.hideBalances
                            ? HIDDEN
                            : record.symbol === 'IRT'
                              ? `${priceDigit(record?.available?.toFixed(0))} تومان`
                              : '-'}
                    </span>
                ) : (
                    '-'
                ),
        },
        {
            title: 'مقدار تومانی',
            dataIndex: 'value',
            key: 'value',
            align: 'center',
            render: (text, record: Asset) => (
                <span>
                    {props.hideBalances
                        ? HIDDEN
                        : record.symbol === 'IRT'
                          ? `${priceDigit(record?.value?.toFixed(0))} تومان`
                          : '-'}
                </span>
            ),
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            render: (text, record: Asset) => (
                <div className={'flex items-center gap-6'}>
                    {record.symbol?.toUpperCase() === 'IRT' && (
                        <>
                            <Button
                                onClick={() => navigate('/wallet/deposit')}
                                className={'h-8 text-sm'}
                                disabled={!record?.isDepositEnable}
                            >
                                واریز
                            </Button>
                            <Button
                                onClick={() => navigate('/wallet/withdrawal')}
                                className={'h-8 text-sm'}
                                disabled={!record?.isWidthrawEnable}
                            >
                                برداشت
                            </Button>
                        </>
                    )}
                    <Button
                        onClick={() => navigate(`/trade?symbol=${record.symbol?.toUpperCase()}`)}
                        className={'h-8 text-sm'}
                        disabled={!record?.isTradeEnable}
                    >
                        معامله
                    </Button>
                </div>
            ),
        },
    ];

    const getRowClassName = (record: any) => {
        switch (record?.symbol?.toUpperCase()) {
            case 'USDT':
                return 'bg-primary-20';
            case 'TRX':
                return 'bg-primary-20';
            default:
                return '';
        }
    };

    const handlePageChange = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
    };

    const handleKeywordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setKeyword(event.target.value);
    };

    const filteredAssets =
        props?.wallet?.assets.filter((asset) => {
            const matchesKeyword =
                asset.name?.includes(keyword) || asset.symbol?.toUpperCase().includes(keyword?.toUpperCase());
            const hasBalance = !hideEmptyWallets || asset.balance > 0 || asset.available > 0;
            return matchesKeyword && hasBalance;
        }) || [];

    return (
        <Spin spinning={props.loading}>
            <div className="flex flex-col gap-6 mt-6">
                <WalletSummary {...props} />
                <div className="grid grid-cols-3 gap-6 max-lg:grid-cols-1">
                    {pinCoinsData !== undefined ? (
                        pinCoinsData?.map((item, index) => <MyWalletBalanceCard key={index} item={item} />)
                    ) : (
                        <>
                            <div className="bg-white border border-primary-20 min-h-40 items-center justify-center rounded-lg flex-1 flex flex-col p-4 w-full h-full box-border">
                                <Spin />
                            </div>
                            <div className="bg-white border border-primary-20 min-h-40 items-center justify-center rounded-lg flex-1 flex flex-col p-4 w-full h-full box-border">
                                <Spin />
                            </div>
                            <div className="bg-white border border-primary-20 min-h-40 items-center justify-center rounded-lg flex-1 flex flex-col p-4 w-full h-full box-border">
                                <Spin />
                            </div>
                        </>
                    )}
                </div>
                <div className={'flex items-center gap-4 mt-6 max-lg:flex-col max-lg:items-start'}>
                    <Input
                        className={'h-12 w-72 max-lg:w-full'}
                        size={'small'}
                        placeholder={'ارز مورد نظر خود را جستجو کنید'}
                        prefix={<CiSearch />}
                        value={keyword}
                        onChange={handleKeywordChange}
                    />
                    <div
                        className={'flex items-center gap-2 clickable'}
                        onClick={() => setHideEmptyWallets(!hideEmptyWallets)}
                    >
                        <Checkbox checked={hideEmptyWallets} onChange={(e) => setHideEmptyWallets(e.target.checked)} />
                        <span className={'text-sm font-bold whitespace-nowrap'}>مخفی کردن موجودی‌های صفر</span>
                    </div>
                </div>
                <Table
                    className={'rounded-[8px] max-lg:hidden'}
                    dataSource={filteredAssets}
                    rowKey={'id'}
                    columns={columns}
                    rowClassName={(record) => getRowClassName(record)}
                    loading={props.loading}
                    pagination={{
                        current: page,
                        pageSize: pageSize,
                        total: filteredAssets?.length,
                        onChange: handlePageChange,
                        onShowSizeChange: handlePageChange,
                        showSizeChanger: true,
                        position: ['bottomCenter'],
                    }}
                />
                <ul className={'flex flex-col gap-4 mt-4 lg:hidden'}>
                    {filteredAssets.slice((page - 1) * pageSize, page * pageSize).map(
                        (item) =>
                            item?.symbol !== 'IRT' && (
                                <li className={'p-4 rounded-[8px] bg-white border border-primary-20'} key={item?.id}>
                                    <div className={'flex items-center gap-2'}>
                                        <CoinIcon symbol={item?.symbol} className={'w-8 h-8'} />
                                        <span className={'text-sm font-bold'}>{item?.name?.toUpperCase()}</span>
                                        <span className={'text-xs font-medium'}>{item?.symbol?.toUpperCase()}</span>
                                    </div>
                                    <Divider />
                                    <div className={'flex justify-between items-center'}>
                                        <div className={'flex flex-col'}>
                                            <p className={'text-sm font-bold'}>موجودی:</p>
                                            <span className={'text-sm font-medium text-darkPurple-60'} dir={'ltr'}>
                                                {props.hideBalances ? HIDDEN : item?.balance} {item?.symbol}
                                            </span>
                                        </div>
                                        <div className={'flex flex-col'}>
                                            <div>
                                                <p className={'text-sm font-bold'}>معادل دلاری:</p>
                                                <span className={'text-sm font-medium text-darkPurple-60'} dir={'ltr'}>
                                                    {props.hideBalances
                                                        ? HIDDEN
                                                        : (item?.balance * item?.lastPrice).toLocaleString()}{' '}
                                                    USDT
                                                </span>
                                            </div>
                                        </div>
                                        <div className={'flex flex-col'}>
                                            <div>
                                                <p className={'text-sm font-bold'}>معادل تومانی:</p>
                                                <span className={'text-sm font-medium text-darkPurple-60'}>
                                                    {props.hideBalances ? HIDDEN : item?.value.toLocaleString()} تومان
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={'flex items-center justify-center mt-4 gap-2'}>
                                        {/* <Button className={'!h-8 text-sm'} disabled={!item?.isDepositEnable}>
                                            واریز
                                        </Button>
                                        <Button className={'!h-8 text-sm'} disabled={!item?.isWidthrawEnable}>
                                            برداشت
                                        </Button> */}
                                        <Button className={'!h-8 text-sm'} disabled={!item?.isTradeEnable}>
                                            معامله
                                        </Button>
                                    </div>
                                </li>
                            ),
                    )}
                </ul>
                <ConfigProvider locale={fa_IR}>
                    <Pagination
                        current={page}
                        pageSize={pageSize}
                        total={filteredAssets?.length}
                        onChange={handlePageChange}
                        className="mt-4 justify-center lg:hidden max-lg:mb-16"
                        style={{ direction: 'ltr' }}
                        showSizeChanger={true}
                        onShowSizeChange={handlePageChange}
                    />
                </ConfigProvider>
            </div>
        </Spin>
    );
};

const mapStateToProps = (state: ReduxState) => ({
    isAuth: state.authStatus,
    user: state.userData,
});

const connector = connect(mapStateToProps);
export default connector(MyWallet);
